/*
 * Lumahex storefront scripts (loaded as a module by every lx-* section; modules run once per page).
 *  <lx-banner>  full-bleed banner; when no image is set it draws an animated scene (crowd / crew / halloween / visor)
 *               on one canvas; its glow is painted small offscreen, blurred and added on top (cheap to scroll).
 *  <lx-stage>   product-page live preview of the LED matrix glasses (color + pattern), plus the product photos.
 *  <lx-packs>   pack cards that drive the theme's (visually hidden) variant picker.
 *  [data-lx-pick] links (pack cards, banners) select a pack on the product page instead of reloading it.
 * The drawn glasses follow the real product: a rectangular frame with a nose arch, hinge tabs at the top corners and a
 * dot-matrix of LEDs (multicolor = blue / pink / white / green quarters).
 */

const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
const BLUE = '#2f7bff';
const PINK = '#ff3daa';
const WHITE = '#eaf2ff';
const GREEN = '#2bff88';
const VIOLET = '#9b5cff';
const RED = '#ff3b3b';
const QUARTERS = [BLUE, PINK, WHITE, GREEN];
const XMAS = [RED, WHITE, GREEN, RED];
const BULBS = [RED, '#ffc94d', GREEN, BLUE, WHITE];
const rand = (a, b) => a + Math.random() * (b - a);

/* glasses are 0.34 × their width tall; with hinge tabs and the floor glow they need about SPAN × width of height */
const SPAN = 0.46;

/* ---------- LED matrix glasses ---------- */

function roundRect(c, x, y, w, h, r) {
  c.beginPath();
  if (c.roundRect) c.roundRect(x, y, w, h, r);
  else c.rect(x, y, w, h);
}

/* rounded rectangle with the nose arch cut out of the bottom edge */
function framePath(cx, cy, w, h, noseW, noseH) {
  const x0 = cx - w / 2;
  const x1 = cx + w / 2;
  const y0 = cy - h / 2;
  const y1 = cy + h / 2;
  const r = h * 0.14;
  const p = new Path2D();
  p.moveTo(x0 + r, y0);
  p.lineTo(x1 - r, y0);
  p.arcTo(x1, y0, x1, y0 + r, r);
  p.lineTo(x1, y1 - r);
  p.arcTo(x1, y1, x1 - r, y1, r);
  p.lineTo(cx + noseW, y1);
  p.bezierCurveTo(cx + noseW * 0.62, y1, cx + noseW * 0.62, y1 - noseH, cx, y1 - noseH);
  p.bezierCurveTo(cx - noseW * 0.62, y1 - noseH, cx - noseW * 0.62, y1, cx - noseW, y1);
  p.lineTo(x0 + r, y1);
  p.arcTo(x0, y1, x0, y1 - r, r);
  p.lineTo(x0, y0 + r);
  p.arcTo(x0, y0, x0 + r, y0, r);
  p.closePath();
  return p;
}

/* build the glasses: frame paths + the LED dots that fit inside the lens (isPointInPath works in device pixels) */
function glasses(ctx, dpr, cx, cy, vw, color, mode, phase, options = {}) {
  const h = vw * 0.34;
  const b = h * 0.085;
  const noseW = vw * 0.12;
  const noseH = h * 0.42;
  const outer = framePath(cx, cy, vw, h, noseW, noseH);
  const inner = framePath(cx, cy, vw - 2 * b, h - 2 * b, noseW + b * 0.9, noseH);
  const cols = 24;
  const rows = 7;
  const iw = vw - 2 * b;
  const ih = h - 2 * b;
  const sx = iw / cols;
  const sy = ih / rows;
  const rad = Math.min(sx, sy) * 0.3;
  const dots = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const x = cx - iw / 2 + sx * (c + 0.5);
      const y = cy - ih / 2 + sy * (r + 0.5);
      if (ctx.isPointInPath(inner, x * dpr, (y + rad) * dpr) && ctx.isPointInPath(inner, x * dpr, (y - rad) * dpr)) dots.push({ x, y, c, r });
    }
  }
  return { outer, inner, dots, cols, rows, rad, color, mode, phase, cx, cy, vw, h, floor: Boolean(options.floor) };
}

const DOLLAR = ['..#..', '.####', '#.#..', '.###.', '..#.#', '####.', '..#..'];

function hash(c, r, k) {
  const s = Math.sin(c * 12.9898 + r * 78.233 + k * 37.719) * 43758.5453;
  return s - Math.floor(s);
}

/* how bright (0..1) a dot is for a pattern: 0 full glow, 1 firework, 2 dollar, 3 arrowhead, 4 wave, 5 sparkle */
function lit(g, d, t) {
  const half = g.cols / 2;
  const right = d.c >= half;
  const lc = right ? d.c - half : d.c;
  switch (g.mode) {
    case 0:
      return 1;
    case 1: {
      const p = (t * 0.8 + (right ? 0.5 : 0)) % 1;
      const dx = lc - (half / 2 - 0.5);
      const dy = d.r - 3;
      const dist = Math.hypot(dx, dy);
      const ray = Math.abs(Math.abs(dx) - Math.abs(dy)) < 0.6 || Math.abs(dx) < 0.6 || Math.abs(dy) < 0.6;
      if (dist < 0.8) return 1 - p * 0.5;
      return ray && Math.abs(dist - (0.8 + p * 6)) < 1 ? 1 - p * 0.75 : 0;
    }
    case 2: {
      const gc = lc - Math.floor((half - 5) / 2);
      const on = gc >= 0 && gc < 5 && DOLLAR[d.r] && DOLLAR[d.r][gc] === '#';
      return on ? 0.55 + 0.45 * (0.5 + 0.5 * Math.sin(t * 3)) : 0;
    }
    case 3: {
      const k = (right ? lc : half - 1 - lc) - t * 6;
      return Math.abs((((k % 6) + 6) % 6) - Math.abs(d.r - 3)) < 0.5 ? 1 : 0;
    }
    case 4: {
      const y = 3 + 2.6 * Math.sin(d.c * 0.45 - t * 4);
      const e = Math.abs(d.r - y);
      return e < 0.55 ? 1 : e < 1.2 ? 0.25 : 0;
    }
    default:
      return hash(d.c, d.r, Math.floor(t * 7)) > 0.7 ? 0.5 + 0.5 * hash(d.r, d.c, 3) : 0;
  }
}

/* color = one hex, 'multi' (the product's blue / pink / white / green quarters) or an array of 4 quarter colors */
function quarterColor(color, q) {
  if (color === 'multi') return QUARTERS[q];
  return Array.isArray(color) ? color[q] : color;
}

function dotColor(g, d) {
  return quarterColor(g.color, Math.min(3, Math.floor((d.c / g.cols) * 4)));
}

function drawGlasses(c, g, v, t, frozen) {
  const time = frozen ? 2.25 : t + v.phase;
  const x0 = v.cx - v.vw / 2;
  const x1 = v.cx + v.vw / 2;
  const y0 = v.cy - v.h / 2;
  /* hinge tabs */
  c.fillStyle = '#0d0f15';
  roundRect(c, x0 - v.vw * 0.045, y0 + v.h * 0.05, v.vw * 0.07, v.h * 0.36, v.h * 0.05);
  c.fill();
  roundRect(c, x1 - v.vw * 0.025, y0 + v.h * 0.05, v.vw * 0.07, v.h * 0.36, v.h * 0.05);
  c.fill();
  /* frame + dark lens */
  c.fillStyle = '#14161d';
  c.fill(v.outer);
  c.fillStyle = '#040509';
  c.fill(v.inner);
  /* LEDs */
  for (const d of v.dots) {
    const L = lit(v, d, time);
    c.beginPath();
    c.arc(d.x, d.y, v.rad, 0, Math.PI * 2);
    if (L > 0.05) {
      const col = dotColor(v, d);
      c.globalAlpha = 0.3 + 0.7 * L;
      c.fillStyle = col;
      c.fill();
      c.globalAlpha = 1;
      g.globalAlpha = L * 0.85;
      g.fillStyle = col;
      g.beginPath();
      g.arc(d.x, d.y, v.rad * 2.3, 0, Math.PI * 2);
      g.fill();
      g.globalAlpha = 1;
    } else {
      c.fillStyle = 'rgba(70, 78, 100, 0.32)';
      c.fill();
    }
  }
  /* soft light spilling onto the surface below */
  if (v.floor) {
    for (let q = 0; q < 4; q++) {
      const col = quarterColor(v.color, q);
      g.globalAlpha = 0.28;
      g.fillStyle = col;
      g.beginPath();
      g.ellipse(x0 + v.vw * (0.14 + q * 0.24), v.cy + v.h * 0.6, v.vw * 0.11, v.h * 0.1, 0, 0, Math.PI * 2);
      g.fill();
      g.globalAlpha = 1;
    }
  }
  /* frame edge + a little gloss */
  c.lineWidth = Math.max(1, v.vw / 320);
  c.strokeStyle = 'rgba(170, 190, 240, 0.3)';
  c.stroke(v.outer);
  c.save();
  c.clip(v.inner);
  const gr = c.createLinearGradient(0, y0, 0, v.cy);
  gr.addColorStop(0, 'rgba(255,255,255,0.08)');
  gr.addColorStop(1, 'rgba(255,255,255,0)');
  c.fillStyle = gr;
  c.fillRect(x0, y0, v.vw, v.h / 2);
  c.restore();
}

/* simple on/off rhythm for the small glasses in the crowd */
function blink(mode, t) {
  switch (mode) {
    case 0:
      return 1;
    case 1:
      return t % 1.2 < 0.6 ? 1 : 0.06;
    case 2:
      return t % 0.3 < 0.15 ? 1 : 0.06;
    default:
      return 0.14 + 0.86 * (0.5 + 0.5 * Math.sin(t * 2.4));
  }
}

/* ---------- one animation loop for everything on the page, capped at 30 fps (plenty for LEDs, half the work) ---------- */

const FRAME_MS = 1000 / 30;
const animated = new Set();
let running = false;
let lastFrame = 0;
const isLive = (item) => item.isConnected && item.visible && !item.paused;
function frame(now) {
  let any = false;
  const due = now - lastFrame >= FRAME_MS - 2;
  if (due) lastFrame = now;
  for (const item of animated) {
    if (!isLive(item)) continue;
    any = true;
    if (due) item.draw(now / 1000);
  }
  running = any;
  if (any) requestAnimationFrame(frame);
}
function kick() {
  if (!running && !reduced) {
    running = true;
    requestAnimationFrame(frame);
  }
}
const io =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) =>
          entries.forEach((e) => {
            e.target.visible = e.isIntersecting;
            if (e.isIntersecting) kick();
          }),
        { rootMargin: '120px' }
      )
    : null;

/* size a canvas to its host; soft LED art does not need full retina resolution, so the pixel ratio is capped */
function sizeCanvas(host, canvas, maxDpr) {
  const r = host.getBoundingClientRect();
  const dpr = Math.min(window.devicePixelRatio || 1, maxDpr);
  canvas.width = Math.round(r.width * dpr);
  canvas.height = Math.round(r.height * dpr);
  canvas.getContext('2d').setTransform(dpr, 0, 0, dpr, 0, 0);
  return { w: r.width, h: r.height, dpr, rect: r };
}

/* The glow is painted on a small offscreen canvas, blurred once while small and added onto the main canvas.
   A full-size glow canvas blurred and blended by CSS (the first version) made scrolling stutter. */
const canFilter = (() => {
  try {
    return typeof document.createElement('canvas').getContext('2d').filter === 'string';
  } catch (e) {
    return false;
  }
})();

class GlowLayer {
  constructor(scale) {
    this.scale = scale;
    this.a = document.createElement('canvas');
    this.b = document.createElement('canvas');
    this.ctx = this.a.getContext('2d');
    this.bctx = this.b.getContext('2d');
  }

  resize(w, h) {
    for (const c of [this.a, this.b]) {
      c.width = Math.max(1, Math.round(w * this.scale));
      c.height = Math.max(1, Math.round(h * this.scale));
    }
    this.ctx.setTransform(this.scale, 0, 0, this.scale, 0, 0);
    this.w = w;
    this.h = h;
  }

  clear() {
    this.ctx.save();
    this.ctx.setTransform(1, 0, 0, 1, 0, 0);
    this.ctx.clearRect(0, 0, this.a.width, this.a.height);
    this.ctx.restore();
  }

  addTo(c, strength) {
    let src = this.a;
    if (canFilter) {
      this.bctx.clearRect(0, 0, this.b.width, this.b.height);
      this.bctx.filter = 'blur(3px)';
      this.bctx.drawImage(this.a, 0, 0);
      this.bctx.filter = 'none';
      src = this.b;
    }
    c.save();
    c.globalCompositeOperation = 'lighter';
    c.globalAlpha = strength;
    c.drawImage(src, 0, 0, this.w, this.h);
    c.restore();
  }
}

/* ---------- banner scenes (T = top/bottom of the copy, so the artwork uses the free space) ---------- */

const SCENES = {
  /* stage beams, one big pair of glasses, a crowd wearing glowing glasses */
  crowd: {
    build(w, h, ctx, dpr, T) {
      const narrow = w < 750;
      let big;
      if (narrow) {
        const vw = Math.min(w * 0.92, (T.top - 30) / SPAN);
        big = glasses(ctx, dpr, w / 2, (T.top - 10) / 2, vw, 'multi', 1, 0, { floor: true });
      } else {
        const vw = Math.min(w * 0.46, h * 1.15);
        big = glasses(ctx, dpr, Math.min(w - 48 - vw / 2, w * 0.72), h * 0.42, vw, 'multi', 1, 0, { floor: true });
      }
      const n = Math.ceil(w / 58);
      const crowd = [];
      for (let i = 0; i < n; i++) {
        const s = rand(24, 42);
        crowd.push({ x: (i + rand(0.1, 0.9)) * (w / n), y: h - rand(40, 110), s, color: [BLUE, PINK, GREEN, VIOLET, WHITE][Math.floor(rand(0, 5))], mode: Math.floor(rand(0, 4)), ph: rand(0, 6) });
      }
      crowd.sort((a, b) => a.y - b.y);
      const beams = [0.1, 0.3, 0.52, 0.74, 0.92].map((x, i) => ({ x: x * w, c: [BLUE, '#22e1ff', VIOLET, BLUE, PINK][i], ph: i * 1.3 }));
      return { big, crowd, beams };
    },
    paint(c, g, w, h, s, t) {
      const bg = c.createLinearGradient(0, 0, 0, h);
      bg.addColorStop(0, '#02040a');
      bg.addColorStop(0.6, '#071230');
      bg.addColorStop(1, '#03050c');
      c.fillStyle = bg;
      c.fillRect(0, 0, w, h);
      for (const bm of s.beams) {
        const a = Math.sin(t * 0.5 + bm.ph) * 0.35;
        const len = h * 1.2;
        const x2 = bm.x + Math.sin(a) * len;
        const spread = w * 0.05;
        const gr = g.createLinearGradient(bm.x, 0, x2, len);
        gr.addColorStop(0, bm.c);
        gr.addColorStop(1, 'rgba(0,0,0,0)');
        g.globalAlpha = 0.22;
        g.fillStyle = gr;
        g.beginPath();
        g.moveTo(bm.x - 4, 0);
        g.lineTo(x2 - spread, len);
        g.lineTo(x2 + spread, len);
        g.lineTo(bm.x + 4, 0);
        g.closePath();
        g.fill();
        g.globalAlpha = 1;
      }
      drawGlasses(c, g, s.big, t);
      for (const p of s.crowd) {
        c.fillStyle = '#01020a';
        c.beginPath();
        c.ellipse(p.x, p.y, p.s * 0.42, p.s * 0.52, 0, 0, Math.PI * 2);
        c.fill();
        c.beginPath();
        c.ellipse(p.x, p.y + p.s * 1.15, p.s * 0.95, p.s * 0.7, 0, Math.PI, 0);
        c.fill();
        c.fillRect(p.x - p.s * 0.95, p.y + p.s * 1.15, p.s * 1.9, h);
        const L = blink(p.mode, t + p.ph);
        if (L > 0.08) {
          const vy = p.y - p.s * 0.08;
          c.globalAlpha = L;
          c.fillStyle = p.color;
          roundRect(c, p.x - p.s * 0.42, vy - p.s * 0.11, p.s * 0.84, p.s * 0.22, p.s * 0.04);
          c.fill();
          c.globalAlpha = 1;
          g.globalAlpha = L;
          g.fillStyle = p.color;
          roundRect(g, p.x - p.s * 0.5, vy - p.s * 0.16, p.s, p.s * 0.32, p.s * 0.08);
          g.fill();
          g.globalAlpha = 1;
        }
      }
    },
  },

  /* four pairs, four colors, four patterns, under the copy (2 × 2 on phones) */
  crew: {
    build(w, h, ctx, dpr, T) {
      const narrow = w < 750;
      const set = [[BLUE, 3], [PINK, 1], [GREEN, 2], [VIOLET, 0]];
      const y0 = T.bottom + 28;
      const room = h - 16 - y0;
      const vs = set.map(([col, mode], i) => {
        let cx;
        let cy;
        let vw;
        if (narrow) {
          vw = Math.min(w * 0.44, (room / 2 - 10) / SPAN);
          cx = w * (i % 2 ? 0.74 : 0.26);
          cy = y0 + room * (i < 2 ? 0.25 : 0.75) - vw * 0.04;
        } else {
          vw = Math.min(w * 0.21, (room - 10) / SPAN);
          cx = w * (0.14 + i * 0.24);
          cy = y0 + room / 2 - vw * 0.04;
        }
        return glasses(ctx, dpr, cx, cy, vw, col, mode, i * 0.7, { floor: true });
      });
      return { vs };
    },
    paint(c, g, w, h, s, t) {
      const bg = c.createLinearGradient(0, 0, 0, h);
      bg.addColorStop(0, '#03050c');
      bg.addColorStop(0.75, '#0a1022');
      bg.addColorStop(1, '#05070e');
      c.fillStyle = bg;
      c.fillRect(0, 0, w, h);
      for (const v of s.vs) drawGlasses(c, g, v, t);
    },
  },

  /* moon, embers, a silhouette in green glasses; copy on the right (desktop) or at the bottom (phones) */
  halloween: {
    build(w, h, ctx, dpr, T) {
      const narrow = w < 750;
      const hx = narrow ? w * 0.5 : w * 0.33;
      const hy = narrow ? Math.min(h * 0.34, T.top * 0.5) : h * 0.55;
      const rx = narrow ? Math.min(w * 0.19, T.top * 0.2) : Math.min(h * 0.17, w * 0.1);
      const ry = rx * 1.28;
      const v = glasses(ctx, dpr, hx, hy - ry * 0.1, rx * 2.3, GREEN, 4, 0);
      const moon = { x: narrow ? w * 0.8 : w * 0.2, y: h * 0.2, r: Math.min(w, h) * (narrow ? 0.12 : 0.14) };
      const embers = Array.from({ length: 40 }, () => ({ x: rand(0, w), y: rand(0, h), r: rand(1, 2.6), sp: rand(8, 24) }));
      const fig = new Path2D();
      fig.ellipse(hx, hy, rx, ry, 0, 0, Math.PI * 2);
      fig.moveTo(hx - rx * 2.4, hy + ry * 2.1);
      fig.ellipse(hx, hy + ry * 2.1, rx * 2.4, ry * 1.2, 0, Math.PI, 0);
      fig.rect(hx - rx * 2.4, hy + ry * 2.1, rx * 4.8, h);
      return { v, moon, embers, fig };
    },
    paint(c, g, w, h, s, t) {
      const bg = c.createLinearGradient(0, 0, 0, h);
      bg.addColorStop(0, '#0b0518');
      bg.addColorStop(0.6, '#160a33');
      bg.addColorStop(1, '#05030b');
      c.fillStyle = bg;
      c.fillRect(0, 0, w, h);
      c.fillStyle = 'rgba(217, 212, 255, 0.85)';
      c.beginPath();
      c.arc(s.moon.x, s.moon.y, s.moon.r, 0, Math.PI * 2);
      c.fill();
      g.globalAlpha = 0.5;
      g.fillStyle = '#b9a8ff';
      g.beginPath();
      g.arc(s.moon.x, s.moon.y, s.moon.r * 1.3, 0, Math.PI * 2);
      g.fill();
      g.globalAlpha = 1;
      for (const e of s.embers) {
        const y = (((e.y - t * e.sp) % h) + h) % h;
        c.fillStyle = 'rgba(255, 140, 60, 0.8)';
        c.beginPath();
        c.arc(e.x, y, e.r, 0, Math.PI * 2);
        c.fill();
        g.fillStyle = 'rgba(255, 140, 60, 0.6)';
        g.beginPath();
        g.arc(e.x, y, e.r * 2.4, 0, Math.PI * 2);
        g.fill();
      }
      /* moonlight rim: stroke first, then fill over it so only the outer edge stays */
      c.lineWidth = 2.4;
      c.strokeStyle = 'rgba(190, 172, 255, 0.32)';
      c.stroke(s.fig);
      c.fillStyle = '#04020a';
      c.fill(s.fig);
      g.lineWidth = 6;
      g.strokeStyle = 'rgba(160, 130, 255, 0.55)';
      g.stroke(s.fig);
      g.fillStyle = '#000';
      g.fill(s.fig);
      drawGlasses(c, g, s.v, t);
    },
  },

  /* Christmas night: string lights, snow, a tree and a figure in a Santa hat wearing red / white / green glasses;
     copy on the right (desktop) or at the bottom (phones) */
  xmas: {
    build(w, h, ctx, dpr, T) {
      const narrow = w < 750;
      const hx = narrow ? w * 0.5 : w * 0.33;
      const hy = narrow ? Math.min(h * 0.38, T.top * 0.58) : h * 0.58;
      const rx = narrow ? Math.min(w * 0.17, T.top * 0.16) : Math.min(h * 0.15, w * 0.09);
      const ry = rx * 1.28;
      const v = glasses(ctx, dpr, hx, hy - ry * 0.1, rx * 2.3, XMAS, 1, 0);
      const fig = new Path2D();
      fig.ellipse(hx, hy, rx, ry, 0, 0, Math.PI * 2);
      fig.moveTo(hx - rx * 2.4, hy + ry * 2.1);
      fig.ellipse(hx, hy + ry * 2.1, rx * 2.4, ry * 1.2, 0, Math.PI, 0);
      fig.rect(hx - rx * 2.4, hy + ry * 2.1, rx * 4.8, h);
      const hat = new Path2D();
      hat.moveTo(hx - rx * 1.02, hy - ry * 0.5);
      hat.quadraticCurveTo(hx - rx * 0.2, hy - ry * 1.75, hx + rx * 1.35, hy - ry * 1.38);
      hat.quadraticCurveTo(hx + rx * 0.6, hy - ry * 1.05, hx + rx * 1.02, hy - ry * 0.5);
      hat.closePath();
      /* string lights hang in swags across the top */
      const swags = narrow ? 2 : 3;
      const lights = [];
      for (let s = 0; s < swags; s++) {
        const a = (s * w) / swags;
        const z = ((s + 1) * w) / swags;
        const n = Math.max(6, Math.round((z - a) / 38));
        for (let i = 0; i <= n; i++) {
          const u = i / n;
          lights.push({ s, x: a + u * (z - a), y: h * 0.05 + Math.sin(u * Math.PI) * h * 0.075, c: BULBS[(s * 7 + i) % BULBS.length], ph: rand(0, 6) });
        }
      }
      const snow = Array.from({ length: narrow ? 70 : 140 }, () => ({ x: rand(0, w), y: rand(0, h), r: rand(0.8, 2.4), sp: rand(10, 34), dr: rand(6, 18), ph: rand(0, 6) }));
      let tree = null;
      if (!narrow) {
        const tx = w * 0.09;
        const top = h * 0.36;
        const half = Math.min(w * 0.075, h * 0.2);
        const bulbs = Array.from({ length: 26 }, () => {
          const u = rand(0.08, 0.95);
          return { x: tx + rand(-1, 1) * half * u * 0.85, y: top + u * (h * 0.92 - top), c: BULBS[Math.floor(rand(0, 5))], ph: rand(0, 6) };
        });
        tree = { tx, top, half, bulbs };
      }
      return { v, fig, hat, hx, hy, rx, ry, lights, snow, tree };
    },
    paint(c, g, w, h, s, t) {
      const bg = c.createLinearGradient(0, 0, 0, h);
      bg.addColorStop(0, '#02050f');
      bg.addColorStop(0.65, '#0a1838');
      bg.addColorStop(1, '#050a18');
      c.fillStyle = bg;
      c.fillRect(0, 0, w, h);
      /* snowy ground */
      c.fillStyle = 'rgba(220, 232, 255, 0.07)';
      c.beginPath();
      c.ellipse(w / 2, h * 1.02, w * 0.75, h * 0.1, 0, 0, Math.PI * 2);
      c.fill();
      /* tree with twinkling lights */
      if (s.tree) {
        const { tx, top, half } = s.tree;
        c.fillStyle = '#02040b';
        for (let k = 0; k < 3; k++) {
          const y0 = top + k * (h - top) * 0.22;
          const y1 = top + (k + 1) * (h - top) * 0.3 + (h - top) * 0.06;
          const hw = half * (0.55 + k * 0.28);
          c.beginPath();
          c.moveTo(tx, y0);
          c.lineTo(tx + hw, y1);
          c.lineTo(tx - hw, y1);
          c.closePath();
          c.fill();
        }
        c.fillRect(tx - half * 0.1, h * 0.9, half * 0.2, h * 0.1);
        for (const b of s.tree.bulbs) {
          const L = 0.35 + 0.65 * (0.5 + 0.5 * Math.sin(t * 2.2 + b.ph));
          c.globalAlpha = L;
          c.fillStyle = b.c;
          c.beginPath();
          c.arc(b.x, b.y, 2.4, 0, Math.PI * 2);
          c.fill();
          c.globalAlpha = 1;
          g.globalAlpha = L * 0.8;
          g.fillStyle = b.c;
          g.beginPath();
          g.arc(b.x, b.y, 7, 0, Math.PI * 2);
          g.fill();
          g.globalAlpha = 1;
        }
        g.globalAlpha = 0.6 + 0.4 * Math.sin(t * 1.5);
        g.fillStyle = '#ffc94d';
        g.beginPath();
        g.arc(tx, top, 9, 0, Math.PI * 2);
        g.fill();
        g.globalAlpha = 1;
      }
      /* figure with a cool rim light, Santa hat, glasses */
      c.lineWidth = 2.4;
      c.strokeStyle = 'rgba(160, 200, 255, 0.3)';
      c.stroke(s.fig);
      c.fillStyle = '#03050c';
      c.fill(s.fig);
      g.lineWidth = 6;
      g.strokeStyle = 'rgba(110, 160, 255, 0.45)';
      g.stroke(s.fig);
      g.fillStyle = '#000';
      g.fill(s.fig);
      c.fillStyle = '#b3122b';
      c.fill(s.hat);
      c.fillStyle = '#e9eef8';
      roundRect(c, s.hx - s.rx * 1.12, s.hy - s.ry * 0.66, s.rx * 2.24, s.ry * 0.24, s.ry * 0.12);
      c.fill();
      c.beginPath();
      c.arc(s.hx + s.rx * 1.35, s.hy - s.ry * 1.38, s.rx * 0.2, 0, Math.PI * 2);
      c.fill();
      drawGlasses(c, g, s.v, t);
      /* string lights: wire, then bulbs */
      c.strokeStyle = 'rgba(30, 40, 60, 0.9)';
      c.lineWidth = 1.5;
      c.beginPath();
      s.lights.forEach((b, i) => {
        if (i === 0 || s.lights[i - 1].s !== b.s) c.moveTo(b.x, b.y);
        else c.lineTo(b.x, b.y);
      });
      c.stroke();
      for (const b of s.lights) {
        const L = 0.45 + 0.55 * (0.5 + 0.5 * Math.sin(t * 2.6 + b.ph));
        c.globalAlpha = L;
        c.fillStyle = b.c;
        c.beginPath();
        c.ellipse(b.x, b.y + 4, 3.2, 4.4, 0, 0, Math.PI * 2);
        c.fill();
        c.globalAlpha = 1;
        g.globalAlpha = L * 0.9;
        g.fillStyle = b.c;
        g.beginPath();
        g.arc(b.x, b.y + 4, 10, 0, Math.PI * 2);
        g.fill();
        g.globalAlpha = 1;
      }
      /* snow in front of everything */
      c.fillStyle = 'rgba(235, 242, 255, 0.85)';
      for (const f of s.snow) {
        const y = (((f.y + t * f.sp) % h) + h) % h;
        const x = f.x + Math.sin(t * 0.8 + f.ph) * f.dr;
        c.beginPath();
        c.arc(x, y, f.r, 0, Math.PI * 2);
        c.fill();
      }
    },
  },

  /* one large pair in the free space beside / above the copy */
  visor: {
    build(w, h, ctx, dpr, T) {
      if (w < 750) {
        const vw = Math.min(w * 0.92, (T.top - 30) / SPAN);
        return { v: glasses(ctx, dpr, w / 2, (T.top - 10) / 2, vw, 'multi', 0, 0, { floor: true }) };
      }
      const vw = Math.min(w * 0.46, h * 1.15);
      return { v: glasses(ctx, dpr, Math.min(w - 48 - vw / 2, w * 0.72), h * 0.46, vw, 'multi', 0, 0, { floor: true }) };
    },
    paint(c, g, w, h, s, t) {
      const bg = c.createRadialGradient(w * 0.65, h * 0.5, 10, w * 0.65, h * 0.5, Math.max(w, h) * 0.7);
      bg.addColorStop(0, '#0b1838');
      bg.addColorStop(1, '#03050c');
      c.fillStyle = bg;
      c.fillRect(0, 0, w, h);
      drawGlasses(c, g, s.v, t);
    },
  },
};

class LxBanner extends HTMLElement {
  connectedCallback() {
    this.scene = SCENES[this.dataset.scene];
    this.base = this.querySelector('canvas.lx-bn__base');
    if (!this.scene || !this.base) return;
    const old = this.querySelector('canvas.lx-bn__glow');
    if (old) old.remove();
    this.glow = new GlowLayer(0.25);
    this.visible = true;
    this.paused = false;
    this.resize();
    animated.add(this);
    if (io) io.observe(this);
    kick();
    this.ro = new ResizeObserver(() => this.resize());
    this.ro.observe(this);
    /* web fonts move the copy, so lay the artwork out again once they are in */
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.resize(true));
  }

  disconnectedCallback() {
    animated.delete(this);
    if (io) io.unobserve(this);
    if (this.ro) this.ro.disconnect();
  }

  resize(force) {
    if (!force && this.data && this.lastW === Math.round(this.offsetWidth) && this.lastH === Math.round(this.offsetHeight)) return;
    const { w, h, dpr, rect } = sizeCanvas(this, this.base, 1.25);
    if (!w || !h) return;
    this.lastW = Math.round(w);
    this.lastH = Math.round(h);
    this.glow.resize(w, h);
    const kids = [...this.querySelectorAll('.lx-bn__copy > *')].map((k) => k.getBoundingClientRect()).filter((k) => k.height > 0);
    const T = kids.length
      ? { top: Math.min(...kids.map((k) => k.top)) - rect.top, bottom: Math.max(...kids.map((k) => k.bottom)) - rect.top }
      : { top: h * 0.6, bottom: h * 0.3 };
    this.w = w;
    this.h = h;
    this.data = this.scene.build(w, h, this.base.getContext('2d'), dpr, T);
    this.draw(performance.now() / 1000);
  }

  draw(t) {
    if (!this.data) return;
    const c = this.base.getContext('2d');
    c.clearRect(0, 0, this.w, this.h);
    this.glow.clear();
    this.scene.paint(c, this.glow.ctx, this.w, this.h, this.data, t);
    this.glow.addTo(c, 0.9);
  }
}
if (!customElements.get('lx-banner')) customElements.define('lx-banner', LxBanner);

/* ---------- product stage: live preview + photos (tap a thumbnail, swipe, or use the arrows) ---------- */

class LxStage extends HTMLElement {
  connectedCallback() {
    this.box = this.querySelector('.lx-stage__box');
    this.base = this.querySelector('canvas.lx-stage__visor');
    this.photo = this.querySelector('.lx-stage__photo');
    this.colors = [...this.querySelectorAll('.lx-dot')];
    this.modes = [...this.querySelectorAll('.lx-mode')];
    this.pauseBtn = this.querySelector('.lx-pause');
    this.thumbs = [...this.querySelectorAll('.lx-thumb')];
    this.counter = this.querySelector('.lx-count');
    if (!this.box || !this.base) return;
    const old = this.querySelector('canvas.lx-stage__glow');
    if (old) old.remove();
    this.glow = new GlowLayer(0.3);
    this.color = (this.colors[0] && this.colors[0].dataset.color) || 'multi';
    this.mode = this.modes[0] ? Number(this.modes[Math.min(1, this.modes.length - 1)].dataset.mode) : 0;
    this.paused = reduced;
    /* picking a color or pattern while a photo is shown switches back to the live preview */
    this.colors.forEach((b, i) => b.addEventListener('click', () => { this.showLive(); this.setColor(i); }));
    this.modes.forEach((b, i) => b.addEventListener('click', () => { this.showLive(); this.setMode(i); }));
    if (this.pauseBtn) this.pauseBtn.addEventListener('click', () => this.togglePause());
    this.thumbs.forEach((b) => b.addEventListener('click', () => this.show(b)));
    this.bindSwipe();
    this.setColor(0);
    this.setMode(Math.min(1, this.modes.length - 1));
    this.syncPause();
    this.updateCount();
    animated.add(this);
    this.box.visible = true;
    if (io) io.observe(this.box);
    this.ro = new ResizeObserver(() => this.resize());
    this.ro.observe(this.box);
    this.resize();
    kick();
    this.preloadAround();
  }

  disconnectedCallback() {
    animated.delete(this);
    if (io && this.box) io.unobserve(this.box);
    if (this.ro) this.ro.disconnect();
  }

  /* animate only while the live preview is on screen and not swapped for a photo */
  get visible() {
    return Boolean(this.box) && this.box.visible !== false && !this.box.classList.contains('is-photo');
  }

  bindSwipe() {
    if (this.thumbs.length < 2) return;
    const prev = this.querySelector('.lx-nav--prev');
    const next = this.querySelector('.lx-nav--next');
    if (prev) prev.addEventListener('click', () => this.step(-1));
    if (next) next.addEventListener('click', () => this.step(1));
    let start = null;
    this.box.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      start = { x: e.clientX, y: e.clientY };
    });
    this.box.addEventListener('pointerup', (e) => {
      if (!start) return;
      const dx = e.clientX - start.x;
      const dy = e.clientY - start.y;
      start = null;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.2) this.step(dx < 0 ? 1 : -1);
    });
    this.box.addEventListener('pointercancel', () => {
      start = null;
    });
    this.box.addEventListener('dragstart', (e) => e.preventDefault());
  }

  currentIndex() {
    return Math.max(0, this.thumbs.findIndex((b) => b.getAttribute('aria-current') === 'true'));
  }

  step(dir) {
    const n = this.thumbs.length;
    const btn = this.thumbs[(this.currentIndex() + dir + n) % n];
    this.show(btn, dir);
    /* keep the active thumbnail in view in the phone swipe row, without moving the page */
    const row = btn.parentElement;
    if (row && row.scrollWidth > row.clientWidth) {
      row.scrollTo({ left: btn.offsetLeft - row.clientWidth / 2 + btn.clientWidth / 2, behavior: reduced ? 'auto' : 'smooth' });
    }
  }

  updateCount() {
    if (this.counter && this.thumbs.length > 1) this.counter.textContent = `${this.currentIndex() + 1} / ${this.thumbs.length}`;
  }

  /* warm the cache for the photos either side so a swipe shows them at once */
  preloadAround() {
    const n = this.thumbs.length;
    if (n < 2) return;
    const i = this.currentIndex();
    for (const k of [i + 1, i - 1, i + 2]) {
      const b = this.thumbs[(k + n) % n];
      if (b.dataset.src && !b.dataset.warm) {
        b.dataset.warm = '1';
        const im = new Image();
        if (b.dataset.srcset) {
          im.sizes = '(min-width: 1200px) 650px, (min-width: 990px) 55vw, 100vw';
          im.srcset = b.dataset.srcset;
        }
        im.src = b.dataset.src;
      }
    }
  }

  setColor(i) {
    const b = this.colors[i];
    if (!b) return;
    this.color = b.dataset.color;
    this.colors.forEach((x, k) => x.setAttribute('aria-pressed', String(k === i)));
    this.style.setProperty('--led', this.color === 'multi' ? BLUE : this.color);
    const ro = this.querySelector('[data-ro="color"]');
    if (ro) ro.textContent = b.getAttribute('aria-label');
    if (this.v) this.v.color = this.color;
    this.redraw();
  }

  setMode(i) {
    const b = this.modes[i];
    if (!b) return;
    this.mode = Number(b.dataset.mode);
    this.modes.forEach((x, k) => x.setAttribute('aria-pressed', String(k === i)));
    const ro = this.querySelector('[data-ro="mode"]');
    if (ro) ro.textContent = b.textContent.trim();
    if (this.v) this.v.mode = this.mode;
    this.redraw();
  }

  togglePause() {
    this.paused = !this.paused;
    this.syncPause();
    this.redraw();
    kick();
  }

  syncPause() {
    if (!this.pauseBtn) return;
    this.pauseBtn.setAttribute('aria-pressed', String(this.paused));
    this.pauseBtn.textContent = this.paused ? this.pauseBtn.dataset.play : this.pauseBtn.dataset.pause;
  }

  showLive() {
    const live = this.thumbs.find((b) => !b.dataset.src);
    if (live && this.box.classList.contains('is-photo')) this.show(live);
  }

  slideIn(dir) {
    if (!dir || reduced) return;
    this.box.classList.remove('lx-in-next', 'lx-in-prev');
    void this.box.offsetWidth;
    this.box.classList.add(dir > 0 ? 'lx-in-next' : 'lx-in-prev');
  }

  show(btn, dir) {
    this.thumbs.forEach((b) => b.setAttribute('aria-current', String(b === btn)));
    this.updateCount();
    const src = btn.dataset.src;
    if (!src) {
      this.box.classList.remove('is-photo');
      this.resize();
      this.slideIn(dir);
      kick();
      return;
    }
    const img = this.photo.querySelector('img');
    const apply = () => {
      img.sizes = '(min-width: 1200px) 650px, (min-width: 990px) 55vw, 100vw';
      img.srcset = btn.dataset.srcset || '';
      img.src = src;
      img.alt = btn.dataset.alt || '';
      this.box.classList.add('is-photo');
      this.slideIn(dir);
    };
    /* swap only once the new photo is decoded, so the frame never flashes empty */
    const next = new Image();
    if (btn.dataset.srcset) {
      next.sizes = '(min-width: 1200px) 650px, (min-width: 990px) 55vw, 100vw';
      next.srcset = btn.dataset.srcset;
    }
    next.src = src;
    const token = (this.token = (this.token || 0) + 1);
    (next.decode ? next.decode() : Promise.resolve()).catch(() => {}).then(() => {
      if (token === this.token) apply();
    });
    this.preloadAround();
  }

  resize() {
    const { w, h, dpr } = sizeCanvas(this.box, this.base, 1.5);
    if (!w || !h) return;
    this.w = w;
    this.h = h;
    this.glow.resize(w, h);
    this.v = glasses(this.base.getContext('2d'), dpr, w / 2, h * 0.46, w * 0.78, this.color, this.mode, 0, { floor: true });
    this.redraw();
  }

  redraw() {
    if (this.v) this.draw(performance.now() / 1000, this.paused);
  }

  draw(t, frozen) {
    if (!this.v) return;
    const c = this.base.getContext('2d');
    c.clearRect(0, 0, this.w, this.h);
    this.glow.clear();
    drawGlasses(c, this.glow.ctx, this.v, t, frozen);
    this.glow.addTo(c, 0.9);
  }
}
if (!customElements.get('lx-stage')) customElements.define('lx-stage', LxStage);

/* ---------- pack cards → theme variant picker ---------- */

class LxPacks extends HTMLElement {
  static card = '.lx-pack';

  connectedCallback() {
    const sel = this.constructor.card;
    this.section = this.closest('.shopify-section') || document;
    this.cards = [...this.querySelectorAll(sel)];
    this.addEventListener('click', (e) => {
      const card = e.target.closest(sel);
      if (!card || card.disabled) return;
      this.pick(card);
    });
    /* any change of the theme picker (ours or the browser restoring a form) re-marks the cards */
    this.section.addEventListener('change', (e) => {
      if (!e.target.closest || !e.target.closest('variant-picker')) return;
      const card = this.cards.find((c) => c.dataset.value === e.target.value);
      if (card) this.mark(card.dataset.variant);
    });
  }

  pick(card) {
    const picker = this.section.querySelector('variant-picker');
    if (picker) {
      const input = [...picker.querySelectorAll('input[type="radio"]')].find((i) => i.value === card.dataset.value);
      if (input && !input.checked) input.click();
      const select = picker.querySelector('select');
      if (!input && select) {
        const opt = [...select.options].find((o) => o.value === card.dataset.value || o.textContent.trim() === card.dataset.value);
        if (opt) {
          select.value = opt.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }
    this.mark(card.dataset.variant);
  }

  mark(id) {
    this.cards.forEach((c) => c.setAttribute('aria-checked', String(c.dataset.variant === id)));
    /* keep the saving chip next to the price in step with the chosen pack */
    const card = this.cards.find((c) => c.dataset.variant === id);
    const chip = this.section.querySelector('[data-lx-save]');
    if (!card || !chip) return;
    const price = Number(card.dataset.price);
    const compare = Number(card.dataset.compare);
    const pct = compare > price ? Math.floor(((compare - price) * 100) / compare) : 0;
    chip.hidden = pct < 1;
    chip.textContent = `${chip.dataset.word} ${pct}%`;
  }
}
if (!customElements.get('lx-packs')) customElements.define('lx-packs', LxPacks);

/* ---------- style cards: the pack-card wiring, plus the style's photo in the stage and its own feature lists ---------- */

class LxStyles extends LxPacks {
  static card = '.lx-style';

  pick(card) {
    super.pick(card);
    const stage = this.section.querySelector('lx-stage');
    const thumb = card.dataset.media && stage && stage.querySelector(`.lx-thumb[data-media="${card.dataset.media}"]`);
    if (thumb && typeof stage.show === 'function') stage.show(thumb);
  }

  mark(id) {
    super.mark(id);
    const card = this.cards.find((c) => c.dataset.variant === id);
    if (!card) return;
    const name = this.section.querySelector('[data-style-name]');
    if (name) name.textContent = card.dataset.value;
    /* styles whose name holds alt_match (e.g. "Cyber") show the second features list and details */
    const match = this.dataset.altMatch;
    if (!match) return;
    const alt = card.dataset.value.includes(match);
    this.section.querySelectorAll('[data-alt-group]').forEach((el) => {
      el.hidden = (el.dataset.altGroup === 'alt') !== alt;
    });
  }
}
if (!customElements.get('lx-styles')) customElements.define('lx-styles', LxStyles);

/* ---------- quantity deals → theme quantity selector (the cart applies the matching automatic discount) ---------- */

class LxQty extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section') || document;
    this.cards = [...this.querySelectorAll('.lx-pack')];
    try {
      this.prices = JSON.parse(this.querySelector('[data-prices]').textContent);
    } catch (e) {
      this.prices = {};
    }
    try {
      this.fmt = new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: this.dataset.currency || 'USD' });
    } catch (e) {
      this.fmt = null;
    }
    this.addEventListener('click', (e) => {
      const card = e.target.closest('.lx-pack');
      if (card) this.pick(Number(card.dataset.qty));
    });
    /* the +/- buttons or typing in the quantity box move the highlight too */
    this.section.addEventListener('quantity-selector:update', (e) => this.mark(Number(e.detail && e.detail.quantity)));
    /* another variant can have another price: recompute the totals once the theme has swapped the variant */
    this.section.addEventListener('change', (e) => {
      if (!(e.target.closest && e.target.closest('variant-picker'))) return;
      setTimeout(() => {
        /* the theme has rendered the new variant's price: take it as the new single-pair state, then re-apply */
        this.orig = null;
        this.reprice();
        const on = this.cards.find((c) => c.getAttribute('aria-checked') === 'true');
        if (on) this.syncTop(Number(on.dataset.qty));
      }, 700);
    });
    /* arrive on the recommended deal: set the theme's quantity once its element is ready */
    const preset = this.cards.find((c) => c.hasAttribute('data-default'));
    if (preset && Number(preset.dataset.qty) > 1) {
      const go = () => this.pick(Number(preset.dataset.qty));
      if (customElements.get('quantity-selector-component')) setTimeout(go, 0);
      else customElements.whenDefined('quantity-selector-component').then(() => setTimeout(go, 0));
    }
  }

  /* cents taken off a deal card of q units at this unit price: "pct" %, "amt" cents, or "free" units */
  offFor(card, price) {
    const q = Number(card.dataset.qty);
    const v = Number(card.dataset.off) || 0;
    switch (card.dataset.offType) {
      case 'pct':
        return Math.floor((price * q * v) / 100);
      case 'amt':
        return v;
      case 'free':
        return price * v;
      default:
        return 0;
    }
  }

  selector() {
    return this.section.querySelector('product-form-component quantity-selector-component') || this.section.querySelector('quantity-selector-component');
  }

  pick(q) {
    const qs = this.selector();
    if (qs && typeof qs.setValue === 'function') {
      qs.setValue(String(q));
      if (typeof qs.onQuantityChange === 'function') qs.onQuantityChange();
      if (typeof qs.updateButtonStates === 'function') qs.updateButtonStates();
    } else {
      const input = this.section.querySelector('input[name="quantity"]');
      if (input) {
        input.value = String(q);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
    this.mark(q);
  }

  mark(q) {
    if (!q) return;
    this.cards.forEach((c) => c.setAttribute('aria-checked', String(Number(c.dataset.qty) === q)));
    this.syncTop(q);
  }

  /* the big price follows the chosen deal: total, the same pairs at the single price struck through, the saving chip */
  syncTop(q) {
    const box = this.section.querySelector('.lx-price');
    const priceEl = box && box.querySelector('product-price [ref="priceContainer"]');
    const chip = box && box.querySelector('[data-lx-save]');
    if (!priceEl) return;
    if (!this.orig) this.orig = { html: priceEl.innerHTML, chipText: chip ? chip.textContent : '', chipHidden: chip ? chip.hidden : true };
    let note = box.querySelector('.lx-price__note');
    const card = this.cards.find((c) => Number(c.dataset.qty) === q);
    const id = this.section.querySelector('form[data-type="add-to-cart-form"] input[name="id"]');
    const unit = (id && this.prices[id.value]) || 0;
    const pct = card && unit ? Math.floor((this.offFor(card, unit) * 100) / (unit * q)) : 0;
    if (!card || q <= 1 || !this.fmt) {
      priceEl.innerHTML = this.orig.html;
      if (chip) {
        chip.textContent = this.orig.chipText;
        chip.hidden = this.orig.chipHidden;
      }
      if (note) note.remove();
      return;
    }
    const total = card.querySelector('[data-total]');
    const full = card.querySelector('[data-full]');
    priceEl.innerHTML = '';
    const add = (cls, text) => {
      const s = document.createElement('span');
      s.className = cls;
      const b = document.createElement('bdi');
      b.textContent = text;
      s.appendChild(b);
      priceEl.appendChild(s);
    };
    add('price-item__group price', total ? total.textContent : '');
    if (full && pct > 0) add('price-item__group price-item--regular compare-at-price', full.textContent);
    if (chip) {
      chip.hidden = pct < 1;
      chip.textContent = `${chip.dataset.word} ${pct}%`;
    }
    if (!note) {
      note = document.createElement('span');
      note.className = 'lx-price__note';
      box.appendChild(note);
    }
    note.textContent = `for ${(card.querySelector('.lx-pack__txt b') || card).firstChild.textContent.trim()}`;
  }

  reprice() {
    const id = this.section.querySelector('form[data-type="add-to-cart-form"] input[name="id"]');
    const price = id && this.prices[id.value];
    if (!price || !this.fmt) return;
    this.cards.forEach((c) => {
      const q = Number(c.dataset.qty);
      const full = price * q;
      const total = full - this.offFor(c, price);
      const put = (sel, cents) => {
        const el = c.querySelector(sel);
        if (el) el.textContent = this.fmt.format(cents / 100);
      };
      put('[data-total]', total);
      put('[data-full]', full);
      put('[data-each]', Math.floor(total / q));
      /* another style can cost more: the saving % and "your 2nd pair is just $X" follow its price */
      const save = c.querySelector('[data-save]');
      if (save) save.textContent = String(Math.floor(((full - total) * 100) / full));
      const note = c.querySelector('.lx-pack__note[data-tpl]');
      if (note) note.textContent = note.dataset.tpl.replace('%s', this.short(total - price));
    });
  }

  /* $25 for whole amounts, $29.99 otherwise (Liquid's money_without_trailing_zeros) */
  short(cents) {
    if (cents % 100 !== 0) return this.fmt.format(cents / 100);
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: this.dataset.currency || 'USD', maximumFractionDigits: 0 }).format(cents / 100);
    } catch (e) {
      return this.fmt.format(cents / 100);
    }
  }
}
if (!customElements.get('lx-qty')) customElements.define('lx-qty', LxQty);

/* ---------- countdown to a real date (local midnight); removes itself once the date has passed ---------- */

class LxCountdown extends HTMLElement {
  connectedCallback() {
    const [y, m, d] = (this.dataset.date || '').split('-').map(Number);
    if (!y || !m || !d) return;
    this.end = new Date(y, m - 1, d).getTime();
    this.cells = Object.fromEntries([...this.querySelectorAll('[data-u]')].map((el) => [el.dataset.u, el]));
    this.tick = this.tick.bind(this);
    if (this.tick()) this.timer = setInterval(this.tick, 1000);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  tick() {
    const left = this.end - Date.now();
    if (left <= 0) {
      this.hidden = true;
      clearInterval(this.timer);
      return false;
    }
    const s = Math.floor(left / 1000);
    const parts = { d: Math.floor(s / 86400), h: Math.floor(s / 3600) % 24, m: Math.floor(s / 60) % 60, s: s % 60 };
    for (const [k, el] of Object.entries(this.cells)) el.textContent = String(parts[k]).padStart(2, '0');
    this.hidden = false;
    return true;
  }
}
if (!customElements.get('lx-countdown')) customElements.define('lx-countdown', LxCountdown);

/* ---------- photo reel: shuffled on every visit, seamless second copy, constant speed ---------- */

class LxReel extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = '1';
    this.track = this.querySelector('.lx-reel__track');
    const group = this.track && this.track.firstElementChild;
    if (!group || !group.children.length) return;
    if (this.hasAttribute('data-shuffle')) {
      const cards = [...group.children];
      for (let i = cards.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [cards[i], cards[j]] = [cards[j], cards[i]];
      }
      cards.forEach((c) => group.appendChild(c));
    }
    if (reduced) return;
    const copy = group.cloneNode(true);
    copy.setAttribute('aria-hidden', 'true');
    copy.querySelectorAll('img').forEach((img) => img.setAttribute('alt', ''));
    copy.querySelectorAll('*').forEach((el) => [...el.attributes].forEach((a) => a.name.startsWith('data-shopify') && el.removeAttribute(a.name)));
    this.track.appendChild(copy);
    this.group = group;
    this.tune = this.tune.bind(this);
    this.tune();
    this.ro = new ResizeObserver(this.tune);
    this.ro.observe(group);
    this.classList.add('is-ready');
  }

  disconnectedCallback() {
    if (this.ro) this.ro.disconnect();
  }

  /* one loop = one copy's width; duration = width / speed, so the pace never depends on how many photos there are */
  tune() {
    const width = this.group.getBoundingClientRect().width;
    const speed = Number(this.dataset.speed) || 40;
    if (width > 0) this.style.setProperty('--lx-reel-dur', `${(width / speed).toFixed(1)}s`);
  }
}
if (!customElements.get('lx-reel')) customElements.define('lx-reel', LxReel);

/* ---------- announcement marquee: constant speed whatever the text length ---------- */

class LxMarquee extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.lx-ann__track');
    if (!this.track) return;
    this.tune = this.tune.bind(this);
    this.tune();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(this.tune);
    this.ro = new ResizeObserver(this.tune);
    this.ro.observe(this);
  }

  disconnectedCallback() {
    if (this.ro) this.ro.disconnect();
  }

  /* the track holds 6 copies and moves by half (3 copies) per loop: duration = that distance / speed */
  tune() {
    const group = this.track.firstElementChild;
    if (!group) return;
    const distance = group.getBoundingClientRect().width * 3;
    const speed = Number(this.dataset.speed) || 45;
    if (distance > 0) this.track.style.animationDuration = `${(distance / speed).toFixed(1)}s`;
  }
}
if (!customElements.get('lx-marquee')) customElements.define('lx-marquee', LxMarquee);

/* links that choose a pack: on the product page select it in place and scroll to the buy box */
document.addEventListener('click', (e) => {
  const a = e.target.closest && e.target.closest('[data-lx-pick]');
  if (!a) return;
  const card = document.querySelector(`lx-packs .lx-pack[data-variant="${a.dataset.lxPick}"]`);
  if (!card) return;
  e.preventDefault();
  card.click();
  card.closest('.lx-buy').scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'center' });
});
